import React, { memo, useCallback, useEffect, useRef, useState } from 'react'

import { Button, ClosingDropdown, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, shape, string } from 'prop-types'

import { STUDIO_EVENT, trackStudioEvent } from 'Pods/ThreeDStudio/Services/studioTracker'
import SoftwareIcon from 'Pods/ThreeDStudio/Shared/SoftwareIcon/SoftwareIcon'
import { getDccPluginDownloadUrl } from 'Pods/ThreeDStudio/Shared/Utils/dccPluginKeys'
import { selectDccSoftware } from 'Pods/ThreeDStudio/Store/actions/dccBridge'
import { openPaidFeatureModal } from 'Pods/ThreeDStudio/Store/actions/ui'

import DccRowSubmenu from './Components/DccRowSubmenu/DccRowSubmenu'
import DccRowTrigger from './Components/DccRowTrigger/DccRowTrigger'

const subPosition = { horizontal: 'right', vertical: 'bottom' }

function DccBridgeRow({
  software,
  pluginKey = '',
  pluginVersion = '',
  isSelected = false,
  isSubmenuOpen = false,
  isConnected = false,
  isLocked = false,
  onSubmenuOpenChange,
  onClosePopover,
}) {
  const [triggerCloseDropdown, setTriggerCloseDropdown] = useState(false)
  const wasOpenRef = useRef(false)

  const hasDownload = Boolean(pluginVersion)
  const downloadUrl = hasDownload ? getDccPluginDownloadUrl(pluginKey) : undefined

  // Close ourselves when the popover decides another row's submenu should open
  // (or when an outside click clears `isSubmenuOpen`).
  useEffect(() => {
    if (wasOpenRef.current && !isSubmenuOpen) {
      setTriggerCloseDropdown(true)
    }
    wasOpenRef.current = isSubmenuOpen
  }, [isSubmenuOpen])

  const handleSelect = useCallback(() => {
    if (isLocked) {
      trackStudioEvent(STUDIO_EVENT.DCC_BRIDGE_CLICK, {
        action: 'select_software',
        software: software.name,
        locked: true,
      })
      openPaidFeatureModal()
      onClosePopover()
      return
    }
    if (software.isEnabled) {
      trackStudioEvent(STUDIO_EVENT.DCC_BRIDGE_CLICK, {
        action: 'select_software',
        software: software.name,
        locked: false,
      })
      selectDccSoftware(software.name)
      onClosePopover()
    }
  }, [isLocked, software, onClosePopover])

  const handleOpenChange = useCallback((open) => {
    onSubmenuOpenChange(software.name, open)
  }, [software.name, onSubmenuOpenChange])

  const stopPropagation = useCallback((e) => {
    e.stopPropagation()
  }, [])

  return (
    <li
      className={classnames(
        'dcc-bridge-popover__row',
        !software.isEnabled && 'dcc-bridge-popover__row--disabled',
        isSelected && 'dcc-bridge-popover__row--selected',
      )}
    >
      <Button
        type="ghost"
        size="small"
        variant="with-icon"
        actionType="button"
        customClass="dcc-bridge-popover__row-main cgt-body-base-semibold"
        icon={<SoftwareIcon iconName={software.iconName} />}
        trailIcon={isConnected
          ? <span className="dcc-bridge-popover__status-dot" aria-label="Connected" />
          : null}
        disabled={!software.isEnabled}
        onClick={handleSelect}
      >
        <span className="dcc-bridge-popover__row-name">{software.name}</span>
      </Button>
      {software.isEnabled && !isLocked && (
        <div onClick={stopPropagation} onKeyDown={stopPropagation} role="presentation">
          <ClosingDropdown
            content={(
              <DccRowSubmenu
                downloadUrl={downloadUrl}
                version={pluginVersion}
                guideLink={software.guideLink}
              />
            )}
            size="medium"
            position={subPosition}
            usePortal
            onOpenChange={handleOpenChange}
            triggerCloseDropdown={triggerCloseDropdown}
            setTriggerCloseDropdown={setTriggerCloseDropdown}
          >
            <DccRowTrigger softwareName={software.name} />
          </ClosingDropdown>
        </div>
      )}
      {/* Coming-soon rows keep a disabled three-dot; locked available rows drop it
          so the whole row reads as a single click target for the paid-feature modal. */}
      {!software.isEnabled && (
        <DccRowTrigger softwareName={software.name} disabled />
      )}
    </li>
  )
}

DccBridgeRow.propTypes = {
  software: shape({
    name: string.isRequired,
    iconName: string.isRequired,
    isEnabled: bool.isRequired,
    guideLink: string,
  }).isRequired,
  pluginKey: string,
  pluginVersion: string,
  isSelected: bool,
  isSubmenuOpen: bool,
  isConnected: bool,
  isLocked: bool,
  onSubmenuOpenChange: func.isRequired,
  onClosePopover: func.isRequired,
}

export default memo(DccBridgeRow)
